import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useGallery } from "../context/GalleryContext";
import { useLightbox } from "../hooks/useLightbox";
import ArtworkCard from "../components/ArtworkCard";
import ArtworkLightbox from "../components/ArtworkLightbox";
import SectionLabel from "../components/SectionLabel";
import { tokens } from "../theme";

const ArrowBack = () => <Box component="span">←</Box>;

export default function CollectionDetail() {
  const { id } = useParams();
  const { artworks, collections } = useGallery();
  const collection = collections.find((c) => c.id === id);
  const pieces = artworks.filter((a) => a.collectionId === id);
  const lb = useLightbox(pieces);

  if (!collection) {
    return (
      <Container maxWidth="md" sx={{ py: 14, textAlign: "center" }}>
        <Typography variant="h2">Collection not found</Typography>
        <Button
          component={RouterLink}
          to="/collections"
          startIcon={<ArrowBack />}
          sx={{ mt: 3, color: tokens.ink }}
        >
          All collections
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 7 } }}>
      <Button
        component={RouterLink}
        to="/collections"
        startIcon={<ArrowBack />}
        sx={{
          color: tokens.taupe,
          pl: 0,
          mb: 3,
          "&:hover": { color: tokens.ink, bgcolor: "transparent" },
        }}
      >
        Collections
      </Button>

      <Box sx={{ maxWidth: 720, mb: { xs: 5, md: 8 } }}>
        <SectionLabel>
          {pieces.length} {pieces.length === 1 ? "piece" : "pieces"}
        </SectionLabel>
        <Typography
          variant="h1"
          sx={{ mt: 2, fontSize: { xs: "2.8rem", md: "4.2rem" } }}
        >
          {collection.name}
        </Typography>
        {collection.description && (
          <Typography sx={{ mt: 2.5, fontSize: "1.1rem" }}>
            {collection.description}
          </Typography>
        )}
      </Box>

      {pieces.length === 0 ? (
        <Typography sx={{ py: 8 }}>
          No pieces in this collection yet.
        </Typography>
      ) : (
        <Box
          sx={{
            columnGap: { xs: 2.5, md: 4 },
            columnCount: { xs: 1, sm: 2, md: 3 },
          }}
        >
          {pieces.map((a) => (
            <Box key={a.id} sx={{ breakInside: "avoid", mb: { xs: 4, md: 5 } }}>
              <ArtworkCard art={a} onOpen={lb.open} />
            </Box>
          ))}
        </Box>
      )}

      <ArtworkLightbox
        art={lb.active}
        collection={collection}
        onClose={lb.close}
        onPrev={lb.prev}
        onNext={lb.next}
      />
    </Container>
  );
}
